import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { AlertTriangle, ExternalLink } from "lucide-react";
import { Decision, SampleBadge } from "../Badges";
import { ago } from "../../lib/api";

const SEV = { red: "border-l-red-500 text-red-300", amber: "border-l-amber-500 text-amber-300", green: "border-l-green-500 text-green-300" };

export const Alerts = ({ alerts }) => (
  <div className="panel p-5" data-testid="crisis-alerts">
    <div className="flex items-center gap-2 mb-4"><AlertTriangle size={15} className="text-red-400" /><span className="eyebrow !text-red-300">Crisis & risk alerts</span><span className="font-data text-xs text-slate-500">({alerts?.length || 0})</span></div>
    <div className="space-y-2 max-h-[340px] overflow-y-auto pr-1">
      {(alerts || []).map((a, i) => (
        <div key={i} className={`border-l-2 pl-3 py-1.5 ${SEV[a.severity] || SEV.amber}`} data-testid={`alert-${i}`}>
          <div className="text-sm text-slate-100">{a.url ? <a href={a.url} target="_blank" rel="noreferrer" className="hover:text-[color:var(--gold)]">{a.title} <ExternalLink size={10} className="inline" /></a> : a.title}</div>
          <div className="font-data text-[10px] text-slate-500 uppercase">{a.type.replace("_", " ")} · {a.source} · {ago(a.at)}</div>
        </div>
      ))}
      {alerts?.length === 0 && <div className="text-sm text-green-400">No active alerts.</div>}
    </div>
    <Decision>Respond fast to legal threats, security incidents or negative sentiment spikes touching DESPITE.</Decision>
  </div>
);

const heat = (v) => `rgba(11,107,58,${0.06 + v * 0.8})`;
export const Heatmap = ({ data }) => (
  <div className="panel p-5" data-testid="utilization-heatmap">
    <div className="eyebrow mb-4">Staff utilisation heatmap · this week</div>
    <div className="overflow-x-auto">
      <table className="w-full text-xs min-w-[520px]">
        <thead><tr><th className="text-left text-slate-500 font-normal pb-2">Dept</th>{(data?.days || []).map((d) => <th key={d.date} className={`font-data font-normal pb-2 ${d.holiday ? "text-gold" : "text-slate-500"}`}>{new Date(d.date).toLocaleDateString("en-GB", { weekday: "short" })}</th>)}<th className="text-slate-500 font-normal pb-2">Tasks</th></tr></thead>
        <tbody>
          {(data?.rows || []).map((r) => (
            <tr key={r.department}>
              <td className="py-1 pr-2 text-slate-300 whitespace-nowrap">{r.department} <span className="text-slate-600">({r.headcount})</span></td>
              {r.days.map((v, i) => <td key={i} className="p-0.5"><div className="h-7 rounded-sm flex items-center justify-center font-data text-[10px]" style={{ background: heat(v), color: v > 0.5 ? "#fff" : "#0E2A1C" }}>{Math.round(v * 100)}</div></td>)}
              <td className={`text-center font-data ${r.task_load >= 0.75 ? "text-red-400" : "text-slate-300"}`}>{Math.round(r.task_load * 100)}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <div className="mt-3"><SampleBadge label="Built on sample roster" /></div>
    <Decision>Rebalance work: see which departments are overloaded or thin on any day.</Decision>
  </div>
);

const COLORS = ["#9FB8AA", "#5E9C7A", "#C9A227", "#B8921F", "#0B6B3A", "#DC2626"];
export const Funnel = ({ r }) => {
  const rows = Object.entries(r?.funnel || {}).map(([k, v]) => ({ name: k.replace("decision_", "").replace("_", " "), v }));
  return (
    <div className="panel p-5" data-testid="recruitment-funnel">
      <div className="eyebrow mb-1">Recruitment funnel</div>
      <div className="flex gap-6 my-3 font-data">
        <div><div className="text-2xl text-ink" data-testid="funnel-applications">{r?.applications ?? 0}</div><div className="text-[10px] text-slate-500">CVs via applications</div></div>
        <div><div className="text-2xl text-ink" data-testid="funnel-pool">{r?.talent_pool ?? 0}</div><div className="text-[10px] text-slate-500">Talent pool</div></div>
        <div><div className="text-2xl text-ink">{r?.open_jobs ?? 0}</div><div className="text-[10px] text-slate-500">Open roles</div></div>
        <div><div className="text-2xl text-ink">{r?.referrals ?? 0}</div><div className="text-[10px] text-slate-500">Referrals</div></div>
      </div>
      <div className="h-44">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} layout="vertical" margin={{ left: 10 }}>
            <XAxis type="number" hide allowDecimals={false} />
            <YAxis type="category" dataKey="name" width={90} tick={{ fill: "#5B7266", fontSize: 11 }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E3EAE5" }} cursor={{ fill: "rgba(11,107,58,.06)" }} />
            <Bar dataKey="v" radius={[0, 2, 2, 0]}>{rows.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}</Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="text-[11px] text-slate-500">Time-to-hire: {typeof r?.time_to_hire_days === "string" ? r.time_to_hire_days : Object.entries(r?.time_to_hire_days || {}).map(([k, v]) => `${k} ${v}d`).join(" · ")}</div>
      <Decision>Know how many Ghanaians want to work for DESPITE and where hiring is stuck.</Decision>
    </div>
  );
};
